import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { CountryComparisonMatrix } from "@/components/compare/CountryComparisonMatrix";
import { comparisonLegend } from "@/components/compare/comparisonConfig";
import { buildCountryReasoningMap, filterDistinctIdeaList } from "@/lib/reasoningQuality";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompareCountries({ user }) {
  const navigate = useNavigate();
  const [defaultComparedCountries, setDefaultComparedCountries] = useState([]);
  const [comparedCountries, setComparedCountries] = useState([]);
  const [comparisonSummary, setComparisonSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const planAccess = user?.plan_access || {};
  const hasPaidPlan = Boolean(user?.has_paid);

  useEffect(() => {
    document.title = "Compare Your Finalists | Relocation Clarity";
    return () => {
      document.title = "Relocation Clarity";
    };
  }, []);

  useEffect(() => {
    const fetchComparison = async () => {
      try {
        const response = await axios.get(`${API}/compare-countries`, { withCredentials: true });
        const recommendedCountries = response.data.countries.filter((country) => country?.code);
        setDefaultComparedCountries(recommendedCountries);
        setComparedCountries(recommendedCountries);
        setComparisonSummary(response.data.summary || {});
      } catch (error) {
        if (error.response?.status === 400) {
          navigate("/assessment", { replace: true });
          return;
        }
        navigate("/assessment-results", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchComparison();
  }, [navigate]);

  const hasHiddenCountries =
    defaultComparedCountries.length > 0 && comparedCountries.length < defaultComparedCountries.length;

  const handleRemoveCountry = (countryCode) => {
    if (comparedCountries.length <= 1) return;
    setComparedCountries((currentCountries) =>
      currentCountries.filter((country) => country?.code && country.code !== countryCode)
    );
  };

  const handleResetComparison = () => {
    setComparedCountries(defaultComparedCountries);
  };

  const reasoningMap = useMemo(
    () => buildCountryReasoningMap(comparedCountries, comparisonSummary, { surface: "compare", fitLimit: 1, tradeoffLimit: 1 }),
    [comparedCountries, comparisonSummary]
  );
  const highestScoreCountry = useMemo(() => {
    if (!comparedCountries.length) return null;
    return [...comparedCountries].sort((a, b) => Number(b?.overall_score || 0) - Number(a?.overall_score || 0))[0] || null;
  }, [comparedCountries]);
  const leadCountry = comparedCountries[0]?.name || "";
  const closestAlternative = comparedCountries[1]?.name || "";
  const leadMismatchNote = useMemo(() => {
    if (!comparedCountries.length || !highestScoreCountry || highestScoreCountry.code === comparedCountries[0]?.code) {
      return "";
    }

    const leadPlanSummary = comparedCountries[0]?.plan_role_summary || comparedCountries[0]?.role_summary || "";
    const strongestAlternativeName = highestScoreCountry.name || "This country";
    const currentLeadName = comparedCountries[0]?.name || "the current lead";

    return `${strongestAlternativeName} comes out strongest in this matrix, but ${currentLeadName} still looks like the stronger overall lead in the broader decision. ${leadPlanSummary}`.trim();
  }, [comparedCountries, highestScoreCountry]);
  const comparisonIntro = leadCountry && closestAlternative
    ? `Start with ${leadCountry} and ${closestAlternative}. Read the tradeoffs row by row, and use the numbers as context rather than the answer on their own.`
    : "Start with the strongest pair in this set. Read the tradeoffs row by row, and use the numbers as context rather than the answer on their own.";
  const displayReasoningMap = useMemo(() => {
    const usedIdeas = [];

    return comparedCountries.reduce((acc, country) => {
      const countryReasoning = reasoningMap[country.code] || {};
      const priorityAnchoredSummary = country.plan_role_summary || country.role_summary || "";
      const fitCandidates = [priorityAnchoredSummary, ...(countryReasoning.fitReasons || [])].filter(Boolean);
      const fitReason = filterDistinctIdeaList(fitCandidates, [comparisonIntro, ...usedIdeas], 1)[0] || "";
      const tradeoffReason = filterDistinctIdeaList(countryReasoning.tradeoffReasons || [], [comparisonIntro, fitReason, ...usedIdeas], 1)[0] || "";

      if (fitReason) usedIdeas.push(fitReason);
      if (tradeoffReason) usedIdeas.push(tradeoffReason);

      acc[country.code] = {
        fitReasons: fitReason ? [fitReason] : [],
        tradeoffReasons: tradeoffReason ? [tradeoffReason] : [],
      };

      return acc;
    }, {});
  }, [comparedCountries, reasoningMap, comparisonIntro]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="compare-countries-loading">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="rc-page-shell min-h-screen bg-background flex flex-col" data-testid="compare-countries-page">
      <Header />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1480px] px-4 py-10 sm:px-6 lg:px-8">
          <section className="rc-panel mb-8 p-8 sm:p-10" data-testid="compare-countries-header">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
              <div>
                <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="compare-countries-header-accent" />
                <p className="rc-section-kicker mb-3">Personalized Blueprint</p>
                <h1 className="font-manrope mt-3 text-4xl text-stone-900 sm:text-5xl" data-testid="compare-countries-title">
                  Compare Your Finalists
                </h1>
                <p className="mt-4 text-sm leading-7 text-stone-500" data-testid="compare-countries-description">
                  This part of your Blueprint puts your strongest options side by side, so you can see the tradeoffs more clearly before choosing a direction.
                </p>
              </div>
              <div className="rounded-[1.6rem] border border-stone-200/80 bg-white/70 p-5 shadow-[0_12px_32px_rgba(28,25,23,0.04)]" data-testid="compare-update-assessment-helper">
                <p className="text-sm font-medium text-stone-900">Not seeing the comparison you expected?</p>
                <p className="mt-2 text-sm leading-7 text-stone-600">
                  If this shortlist feels off, update your assessment before spending time comparing the wrong finalists.
                </p>
                {hasPaidPlan ? (
                  <p className="mt-2 text-sm leading-7 text-stone-600" data-testid="compare-plan-refresh-helper-copy">
                    {planAccess.requires_new_purchase_for_refresh
                      ? "You have already used your included Blueprint refresh. If you update your assessment again and want updated paid guidance, you’ll need a new Blueprint."
                      : "Your paid Relocation Blueprint includes one free refresh. If you update your assessment, we’ll generate a refreshed Blueprint at no extra charge."}
                  </p>
                ) : null}
                <Link
                  to="/assessment?step=review"
                  className="rc-button-outline mt-4 inline-flex px-4 py-2"
                  data-testid="compare-update-assessment-link"
                >
                  Update Assessment
                </Link>
              </div>
            </div>
          </section>

          <div className="rc-panel-soft mb-6 p-5 sm:p-6" data-testid="comparison-toolbar">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-3" data-testid="comparison-legend">
                {comparisonLegend.map((item) => (
                  <div
                    key={item.label}
                    className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-600"
                    data-testid={`legend-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${item.toneClass}`} aria-hidden="true" />
                    <span>
                      <strong className="text-stone-900">{item.label}</strong> — {item.description}
                    </span>
                  </div>
                ))}
              </div>
              {hasHiddenCountries && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetComparison}
                  className="rc-button-outline px-4 py-2"
                  data-testid="reset-comparison-button"
                >
                  Reset Comparison
                </Button>
              )}
            </div>
          </div>

          <p className="mb-5 text-sm leading-7 text-stone-600" data-testid="compare-countries-guidance">
            Compare your finalists side by side across monthly cost, healthcare access, residency realism, and day-to-day livability before you move forward.
          </p>
          <p className="mb-5 text-xs leading-6 text-stone-500" data-testid="compare-countries-input-role-note">
            Your core priorities are doing most of the ranking work here. Motivations and lifestyle refiners help explain the kind of move you are trying to make and can help separate closer calls when the bigger practical factors are already close.
          </p>

          {leadMismatchNote ? (
            <div className="mb-5 rounded-[1.5rem] border border-amber-200 bg-amber-50/75 px-5 py-4 text-sm leading-7 text-stone-700" data-testid="compare-lead-mismatch-note">
              {leadMismatchNote}
            </div>
          ) : null}

          <CountryComparisonMatrix countries={comparedCountries} onRemoveCountry={handleRemoveCountry} reasoningMap={displayReasoningMap} />

          <div className="mt-8" data-testid="compare-countries-footer-link-wrapper">
            <Link
              to="/plan"
              className="rc-button-outline inline-flex px-4 py-2"
              data-testid="back-to-assessment-results-link"
            >
              Back to Your Relocation Blueprint
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}